"use client";

import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import {
	mutationOptions,
	useMutation,
	useSuspenseQuery,
} from "@tanstack/react-query";

import type { Operation } from "@workspace/operations";
import { CheckOperation } from "@workspace/operations";
import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { Input } from "@workspace/ui/components/input";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { courseManagementApi } from "../api";
import {
	defaultCourseBadgeColours,
	type UpdateCourseBadgeColoursInput,
} from "../types";

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const schema = z.object({
	notStarted: z
		.string()
		.regex(HEX_PATTERN, "Must be a 6-digit hex colour code (e.g. #9CA3AF)"),
	inProgress: z
		.string()
		.regex(HEX_PATTERN, "Must be a 6-digit hex colour code (e.g. #F59E0B)"),
	allSent: z
		.string()
		.regex(HEX_PATTERN, "Must be a 6-digit hex colour code (e.g. #22C55E)"),
});

type FormValues = z.infer<typeof schema>;

const fieldMeta: {
	name: keyof FormValues;
	label: string;
	description: string;
}[] = [
	{
		name: "notStarted",
		label: "Not started",
		description: "Shown on tab cards before any course has been fired.",
	},
	{
		name: "inProgress",
		label: "In progress",
		description: "Shown while courses are being fired, between rounds.",
	},
	{
		name: "allSent",
		label: "All courses sent",
		description: "Shown once every standard course has been fired.",
	},
];

interface CourseBadgeColoursFormProps {
	tenant: string;
	venueId: string;
	operations: Operation[];
}

export function CourseBadgeColoursForm({
	tenant,
	venueId,
	operations,
}: CourseBadgeColoursFormProps) {
	const query = useSuspenseQuery(
		courseManagementApi.getCourseBadgeColoursOptions(tenant, venueId),
	);

	const mutation = useMutation(
		mutationOptions({
			...courseManagementApi.updateCourseBadgeColoursOptions(tenant, venueId),
			onSuccess: (result, _variables, _onMutateResult, context) => {
				if (result.success) {
					toast.success("Course badge colours updated.");
					context.client.invalidateQueries({
						queryKey: [tenant, "courses", "badge-colours", venueId],
					});
				} else {
					const errorDetails = result.details as
						{ detail?: string } | undefined;
					toast.error(
						errorDetails?.detail ||
							"Failed to update badge colours. Please try again.",
					);
				}
			},
			onError: () => {
				toast.error("Unable to update badge colours. Please try again.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			notStarted: query.data.notStarted,
			inProgress: query.data.inProgress,
			allSent: query.data.allSent,
		} as FormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: schema,
		},
		onSubmit: async ({ value }) => {
			const input: UpdateCourseBadgeColoursInput = {
				notStarted: value.notStarted,
				inProgress: value.inProgress,
				allSent: value.allSent,
			};
			await mutation.mutateAsync(input);
		},
	});

	const handleReset = async () => {
		form.setFieldValue("notStarted", defaultCourseBadgeColours.notStarted);
		form.setFieldValue("inProgress", defaultCourseBadgeColours.inProgress);
		form.setFieldValue("allSent", defaultCourseBadgeColours.allSent);
		await mutation.mutateAsync({ ...defaultCourseBadgeColours });
	};

	return (
		<CheckOperation
			operation={["venue-management-manage-courses"]}
			operations={operations}
		>
			<Card>
				<CardHeader>
					<CardTitle>Course badge colours</CardTitle>
					<CardDescription>
						Customise the badge colours shown on Staff Terminal tab cards as
						courses are fired. Values must be 6-digit hex codes (e.g. #22C55E).
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						{fieldMeta.map((meta) => (
							<form.AppField key={meta.name} name={meta.name}>
								{(field) => (
									<field.FormItem>
										<field.FormLabel>{meta.label}</field.FormLabel>
										<field.FormControl>
											<div className="flex items-center gap-2">
												<input
													type="color"
													aria-label={`${meta.label} colour picker`}
													className="size-10 cursor-pointer rounded border border-input bg-background p-1"
													value={field.state.value}
													onChange={(e) =>
														field.handleChange(e.target.value.toUpperCase())
													}
												/>
												<Input
													name={field.name}
													aria-label={`${meta.label} hex value`}
													className="font-mono uppercase max-w-[140px]"
													value={field.state.value}
													onChange={(e) =>
														field.handleChange(e.target.value.toUpperCase())
													}
													onBlur={field.handleBlur}
												/>
											</div>
										</field.FormControl>
										<field.FormDescription>
											{meta.description}
										</field.FormDescription>
										<field.FormMessage />
									</field.FormItem>
								)}
							</form.AppField>
						))}

						<div className="flex justify-end gap-2 pt-2">
							<Button
								type="button"
								variant="outline"
								onClick={handleReset}
								disabled={mutation.isPending}
							>
								<RotateCcw className="size-4" />
								Reset to defaults
							</Button>
							<form.Subscribe
								selector={(state) => [state.canSubmit, state.isSubmitting]}
							>
								{([canSubmit]) => (
									<Button
										type="submit"
										disabled={!canSubmit || mutation.isPending}
									>
										{mutation.isPending && (
											<Loader2 className="size-4 animate-spin" />
										)}
										Save colours
									</Button>
								)}
							</form.Subscribe>
						</div>
					</form>
				</CardContent>
			</Card>
		</CheckOperation>
	);
}
